import { ReactHTMLElement } from "react";
import axios from "axios";
import dayjs from "dayjs";

/**
 * Fetch data function
 * @param {Object} parameters - Params object
 * @param {string} url - URL address
 * @param {string} type - HTTP request method
 * @returns {Promise} - promise to be resolved or rejected
 */
export const fetchData = async (parameters, url, type) => {
  return axios({
      method: type,
      url: url,
      data: JSON.stringify({ 
          ...parameters
      }),
  })
  .then(res => res.data)
  .catch(error => console.error(error));
}

/**
 * Applies formatting to translation text
 * @param {string} line - Text value
 * @param {function} callback
 * @returns {ReactHTMLElement[]} - Values to be rendered with the formatting
 */
export const formatTranslation = (line, callback) => {
  const regex = /{:(.+?) (.+?)}/g;

  const applyStyles = (tags, text, key) => {
    const styles = {
      ":b": (content) => <b key={`${key}-b`}>{content}</b>,
      ":u": (content) => <u key={`${key}-u`}>{content}</u>,
      ":s": (content) => <s key={`${key}-s`}>{content}</s>,
      ":i": (content) => <i key={`${key}-i`}>{content}</i>,
      ":a": (content) => <a href="#" onClick={callback} key={`${key}-a`}>{content}</a>,
    };

    return tags.split(":").filter(Boolean).reduce((styledText, tag) => {
      const wrapper = styles[":" + tag];
        
      return wrapper ? wrapper(styledText) : styledText;

    }, text);
  };

  const result = [];
  let lastIndex = 0;
  let keyIndex = 0;

  line.replace(regex, (match, tags, text, offset) => {
    if (offset > lastIndex) {
      result.push(
        <span key={`text-${keyIndex++}`}>
          {line.slice(lastIndex, offset)}
        </span>
      );
    }

    result.push(applyStyles(tags, text, `styled-${keyIndex++}`));

    lastIndex = offset + match.length;
    return match;
  });

  if (lastIndex < line.length) {
    result.push(
      <span key={`text-${keyIndex++}`}>
        {line.slice(lastIndex)}
      </span>
    );
  }

  return result;
}

export function sortByField(data, field, reverse = false) {
  const sortedData = [...data];
  sortedData.sort((a, b) => {
    let valueA = a[field];
    let valueB = b[field];

    if (isDateTime(valueA) && isDateTime(valueB)) {
      valueA = new Date(valueA);
      valueB = new Date(valueB);
    }

    let comparison = 0;

    if (valueA > valueB) {
      comparison = 1;
    } else if (valueA < valueB) {
      comparison = -1;
    }

    return reverse ? -comparison : comparison;
  });

  return sortedData;
}

function isDateTime(value) {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/.test(value);
}

const isValidDate = (value) => {
  return value instanceof Date && !isNaN(value.getTime());
}

/**
 * Formats dates in a standardized way
 * @param {string | Object} date - Value to be formated
 * @param {Object} [options] - Optional params
 * @param {boolean} [options.timeOnly] - Whether to output only the time
 * @param {boolean} [options.dateOnly] - Whether to output only the date
 * @returns {string} Formatted value
 */
export const formatDateDefault = (date, options) => {
  try {

    if (!date) {
      console.warn("No date provided");
      return "-";
    }

    const dateObj = new Date(date);
  
    if (!isValidDate(dateObj)) {
      console.warn("Invalid argument passed into date-formatting function.", { date });
      return "-";
    } else {
      //check for time, non zeros.
      const shouldIncludeTime = /\d{2}:\d{2}(:\d{2})?/.test(date) && (dateObj.getHours() > 0 || dateObj.getMinutes() > 0)
      const standardTimeFormat = "HH:mm:ss";
      const standardDateFormat = "DD-MMM-YYYY";

      if (options?.dateOnly && options?.timeOnly) {
        console.warn("Options should contain either timeOnly or dateOnly, never both. Returning default format.");
        return dayjs(date).format(`${standardDateFormat} ${standardTimeFormat}`);
      }

      if (options?.timeOnly) return dayjs(date).format(standardTimeFormat);
      if (options?.dateOnly || !shouldIncludeTime) return dayjs(date).format(standardDateFormat);
      return dayjs(date).format(`${standardDateFormat} ${standardTimeFormat}`);
    }
  } catch (error) {
    console.error(`An error occurred when trying to format the date ${date}:`, error);
    return "-";
  }
};

export const isEmailInvalid = (email) => !String(email).toLowerCase().match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);

export const isExpiredDate = date => {
  const now = dayjs();
  const givenDate = dayjs(date);
  return givenDate.isBefore(now);
}

/**
 * Gets a cookie value by name
 * @param {string} cookieName - Cookie name to retrieve
 * @returns {string|undefined} - Cookie value if found, undefined otherwise
 */
export const getCookie = (cookieName) => {
  if (typeof document === 'undefined') return undefined;
  
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${cookieName}=`))
    ?.split('=')[1];
};

/**
 * Gets CSRF token from cookies
 * @param {string} [cookieName] - Optional cookie name, defaults to common CSRF cookie names
 * @returns {string|undefined} - CSRF token if found, undefined otherwise
 */
export const getCSRFToken = (cookieName) => {
  if (typeof document === 'undefined') return undefined;
  
  // If specific cookie name provided, use it
  if (cookieName) {
    return getCookie(cookieName);
  }
  
  // Try common CSRF cookie names
  const commonNames = ['csrf-token', 'XSRF-TOKEN', '_csrf', 'csrfToken', 'CSRF-TOKEN'];
  for (const name of commonNames) {
    const token = getCookie(name);
    if (token) return token;
  }
  
  return undefined;
}

/**
 * Gets session ID from cookies or localStorage
 * @returns {string|undefined} - Session ID if found, undefined otherwise
 */
export const getSessionId = () => {
  // First try localStorage (set from auth response)
  const sessionIdFromStorage = localStorage.getItem('sessionId');
  if (sessionIdFromStorage) return sessionIdFromStorage;
  
  // Then try cookies (common session cookie names)
  if (typeof document !== 'undefined') {
    const commonNames = ['sessionId', 'session-id', 'Session', 'SESSION', 'sid'];
    for (const name of commonNames) {
      const sessionId = getCookie(name);
      if (sessionId) return sessionId;
    }
  }
  
  return undefined;
}

/**
 * Generates a device fingerprint
 * @returns {string} - Device fingerprint
 */
export async function generateDeviceFingerprint() {
  const data = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    webdriver: navigator.webdriver,
    screen: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cookiesEnabled: navigator.cookieEnabled,
  };
  const encoder = new TextEncoder();
  const buffer = encoder.encode(JSON.stringify(data));
  const hash = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}